import api from './api'
import {
  DjangoTokenPairDto,
  DjangoTokenRefreshDto,
  DjangoLoginRequest,
} from '../types/django'
import { getTenant } from '../utils/tenant'
import { CurrentUserDto } from '../types/django/user'

export interface MfaRequiredResponseDto {
  mfa_required: true
  mfa_token: string
}
export interface ProvisioningStatusResponseDto {
  status: 'provisioning' | 'ready' | 'failed'
  label?: string
  message?: string
}
export interface ChangePasswordPayload {
  current_password: string;
  new_password: string;
}

export interface VerifyActivationCodeResponseDto {
  valid: boolean
  temp_token?: string 
  tempToken?: string 
  message?: string
}

export interface CompleteActivationResponseDto {
  message?: string
  mfa_token?: string
}

export interface SimpleMessageResponseDto {
  message: string
}

const getAccessKey = () => `access_token_${getTenant()}`
const getRefreshKey = () => `refresh_token_${getTenant()}`
const getUserKey = () => `auth_user_${getTenant()}`

export const authService = {
  // LOGIN CLASSIQUE
 // authService.ts
async login(credentials: DjangoLoginRequest, rememberMe: boolean = true): Promise<DjangoTokenPairDto | MfaRequiredResponseDto> {
  const response = await api.post<DjangoTokenPairDto | MfaRequiredResponseDto>(
    'auth/token/',
    credentials
  )
  const data = response.data

  if ('access' in data && 'refresh' in data) {
    this.setTokens(data, rememberMe) // <--- Transmettre rememberMe ici
  }

  return data
},

  // VALIDATION OTP (Délivre les jetons JWT)
  async verifyOtp(payload: { mfa_token: string; otp_code: string }): Promise<DjangoTokenPairDto> {
    const response = await api.post<DjangoTokenPairDto>('auth/verify-otp/', payload)
    const tokens = response.data
    this.setTokens(tokens)
    return tokens
  },

  // FETCH USER
// ✅ Dans authService.ts
async fetchUser(): Promise<CurrentUserDto> {
  const response = await api.get<CurrentUserDto>('auth/me/')
  const user = response.data

  // Détermine dynamiquement le bon stockage en fonction d'où se trouve le token actuel
  const storage = localStorage.getItem(getAccessKey()) ? localStorage : sessionStorage;
  storage.setItem(getUserKey(), JSON.stringify(user));

  return user
},

  // REFRESH TOKEN
  async refreshAccessToken(): Promise<string | null> {
    const refresh = this.getRefreshToken()
    if (!refresh) return null

    try {
      const response = await api.post<DjangoTokenRefreshDto>(
        'auth/token/refresh/',
        { refresh }
      )
      const { access } = response.data
      localStorage.setItem(getAccessKey(), access)
      return access
    } catch {
      this.clearAuth()
      return null
    }
  },

 // SIGNUP
 async signup(data: {
    name: string
    email: string
  }): Promise<{ message?: string; tenant: { id: number | string; domain: string; schema: string } }> {
    // Nettoyer toute ancienne session avant l'inscription
    this.clearAuth()

    const response = await api.post('auth/signup/', data)
    const { tenant, message } = response.data

    // NE PAS faire this.setTokens(tokens) afin de forcer l'étape d'activation

    return { tenant, message }
  },

// PROVISIONING STATUS (Utilise l'ID du tenant dans l'URL)
  async getProvisioningStatus(tenantId: number | string): Promise<ProvisioningStatusResponseDto> {
    const response = await api.get<ProvisioningStatusResponseDto>(`tenant/${tenantId}/provisioning-status/`)
    return response.data
  },
  
  // VÉRIFICATION DU CODE D'ACTIVATION (Étape 1)
async verifyActivationCode(payload: { email: string; code: string }): Promise<VerifyActivationCodeResponseDto> {
  const response = await api.post<VerifyActivationCodeResponseDto>('auth/verify-activation-code/', {
    email: payload.email,
    code: payload.code,
  })
  
  // Normalisation de la réponse JS
  const data = response.data
  return {
    ...data,
    tempToken: data.temp_token || data.tempToken
  }
},

  // RENVOI DU CODE D'ACTIVATION
  async resendActivationCode(data: { email: string }): Promise<SimpleMessageResponseDto> {
    const response = await api.post<SimpleMessageResponseDto>('auth/resend-activation-code/', data)
    return response.data
  },

 
 // CRÉATION DU MOT DE PASSE ET ACTIVATION DU COMPTE (Étape 2)

async completeActivation(data: {
  email: string
  code: string      
  newPassword: string
}): Promise<CompleteActivationResponseDto> {
  const response = await api.post<CompleteActivationResponseDto>('auth/complete-activation/', {
    email: data.email,
    code: data.code, 
    password: data.newPassword,
  })
  return response.data
},

  // LOGOUT
  logout(): void {
    this.clearAuth()
    window.location.href = '/login'
  },

  // GESTION DES TOKENS
setTokens(tokens: DjangoTokenPairDto, rememberMe: boolean = true): void {
    // Si l'utilisateur veut qu'on se souvienne de lui, on utilise localStorage
    // Sinon, on utilise sessionStorage (effacé à la fermeture de l'onglet/navigateur)
    const storage = rememberMe ? localStorage : sessionStorage;

    storage.setItem(getAccessKey(), tokens.access);
    storage.setItem(getRefreshKey(), tokens.refresh);
  },

  getAccessToken(): string | null {
    // On vérifie d'abord dans le localStorage, puis dans le sessionStorage
    return localStorage.getItem(getAccessKey()) || sessionStorage.getItem(getAccessKey());
  },

  getRefreshToken(): string | null {
    return localStorage.getItem(getRefreshKey()) || sessionStorage.getItem(getRefreshKey());
  },

  // USER CACHE
  getStoredUser(): CurrentUserDto | null {
    try {
      const raw = localStorage.getItem(getUserKey()) || sessionStorage.getItem(getUserKey());
      return raw ? (JSON.parse(raw) as CurrentUserDto) : null;
    } catch {
      return null;
    }
  },

  // CLEAR AUTH (Nettoie les deux stockages par sécurité)
  clearAuth(): void {
    localStorage.removeItem(getAccessKey());
    localStorage.removeItem(getRefreshKey());
    localStorage.removeItem(getUserKey());

    sessionStorage.removeItem(getAccessKey());
    sessionStorage.removeItem(getRefreshKey());
    sessionStorage.removeItem(getUserKey());
  },
// POST /api/password-reset/request/
  requestPasswordReset: async (data: { email: string }) => {
    const response = await api.post('/password-reset/request/', {
      email: data.email,
    })
    return response.data
  },

  // POST /api/password-reset/verify/
  verifyPasswordResetCode: async (data: { email: string; code: string }) => {
    const response = await api.post('/password-reset/verify/', {
      email: data.email,
      code: data.code,
    })
    return response.data
  },

  // POST /api/password-reset/complete/
  completePasswordReset: async (data: { email: string; code: string; password: string }) => {
    const response = await api.post('/password-reset/complete/', {
      email: data.email,
      code: data.code,
      password: data.password,
    })
    return response.data
  },

 changePassword: async (data: { current_password: string; new_password: string }) => {
    const response = await api.post('/change-password/', {
      current_password: data.current_password,
      new_password: data.new_password,
    })
    return response.data
  },
}

export default authService